/* eslint-disable prettier/prettier */
/* eslint-disable react/prop-types */
import PolylineIcon from '@mui/icons-material/Polyline'
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    Typography,
    useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import TemplatePreview from '../../components/TemplatePreview'
import ErrorBanner from '../../components/shared/ErrorBanner'
import { clearTemplateData } from '../../features/templateSlice'
import {
    checkPipelineEventType,
    normalizeEventType,
    pipelineEventType,
    pipelineName,
    trimSpaces,
} from '../../helpers/eventType'
import {
    receiverLabel,
    receiverPreset,
    receiverTypeOptions,
} from '../../helpers/pipelines'
import useTemplatePreview from '../../hooks/useTemplatePreview'
import HolidayService from '../../services/holidayService'
import { errorMessage } from '../../services/axios/errorMessage'
import PipelineService from '../../services/pipelineService'
import TemplateService from '../../services/templateService'
import {
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    TEXT,
    cardSx,
    pageSx,
} from '../../theme/tokens'
import StepBasics from './StepBasics'
import StepChannels from './StepChannels'
import StepReview from './StepReview'
import StepTemplates from './StepTemplates'

const STEPS = [
    'Basics & Trigger',
    'Channels & Audience',
    'Templates',
    'Review & Launch',
]

const EMPTY_FORM = {
    pipeline_name: '',
    event_type: '',
    comment: '',
    niceness: false,
    absolute_time: false,
    // job-delete-inactive-pipelines deletes a one-time pipeline (and its
    // template) 14 days after it is created.
    one_time: false,
    scheduleEnabled: false,
    scheduleSeconds: 0,
    receiverType: '',
    // [{ comm_type, template_id, receiver_type }]
    comms: [],
}

/**
 * Legacy pipelines stored `comms` as a single object — normalize to array.
 *
 * Each comm keeps its WhatsApp `channel` (the sending account: `default`,
 * `dce-12`, `people-ops`). The update moves comms[0].channel to the
 * pipeline's `channel`, which the event processor sends through; it was
 * dropped here, so every edited WhatsApp pipeline fell back to `default`.
 * `pipelineChannel` is the pipeline-level value, for rows whose comm has none.
 */
const normalizeComms = (comms, pipelineChannel) => {
    const list = Array.isArray(comms) ? comms : comms?.comm_type ? [comms] : []
    return list.map((c) => ({
        comm_type: c.comm_type,
        template_id: c.template_id ?? null,
        receiver_type: c.receiver_type ?? '',
        channel: c.channel || pipelineChannel || '',
    }))
}

/**
 * The channel a comm sends through: a WhatsApp template's own channel
 * (`default` when it has none), and none for the other channels, as the old
 * app set it when a template was chosen.
 */
export const commChannel = (commType, template) =>
    commType === 'whatsapp' ? template?.channel || 'default' : ''

/**
 * The PUT /comms/v2/pipeline/{id} body: the saved pipeline with the form's
 * fields on top.
 *
 * The update writes the row it is sent: `created_by`, `one_time`,
 * `fallback_events` and `channel` were not sent and were erased on every
 * edit (the old app sends the whole row too).
 */
export const pipelineEditPayload = (saved, id, fields) => ({
    ...(saved ?? {}),
    ...fields,
    id,
})

/**
 * The pipeline a new holiday needs (from Create Holiday): event type = the
 * holiday ID, Inline audience (job-holiday-reminder sends each recipient's
 * identities in the event), one-time and absolute time, as the applause
 * app created holiday pipelines.
 */
const holidayToPipelineForm = (holiday) => ({
    ...EMPTY_FORM,
    pipeline_name: holiday.holiday_name ?? '',
    event_type: normalizeEventType(holiday.holiday_id),
    absolute_time: true,
    one_time: true,
    receiverType: '__inline',
})

const pipelineToForm = (p) => {
    const comms = normalizeComms(p.comms, p.channel)
    return {
        pipeline_name: p.pipeline_name ?? '',
        event_type: p.event_type ?? '',
        comment: p.comment ?? '',
        niceness: !!p.niceness,
        absolute_time: !!p.absolute_time,
        one_time: !!p.one_time,
        scheduleEnabled: Number(p.schedule) > 0,
        scheduleSeconds: Number(p.schedule) || 0,
        receiverType: comms[0]?.receiver_type ?? '',
        comms,
    }
}

/**
 * Why the receiver type cannot be changed, or '' when it can.
 *
 * A holiday's pipeline sends to the people its holiday event lists (Inline).
 * An existing event type fixes the receiver of its pipelines (`preset`):
 * identities are resolved per receiver type, so another receiver type on the
 * same event would get identities it cannot use (the old app locked it too).
 */
export const receiverLockReason = ({ holiday, preset, form, options }) => {
    if (holiday)
        return 'A holiday pipeline sends to the people its holiday event lists (Inline).'
    if (!preset) return ''
    return form.receiverType === preset
        ? `Pipelines on ${form.event_type} send to ${receiverLabel(
              options,
              preset
          )}, so this one does too. To send to someone else, use a new event type.`
        : `Kept as saved, because ${form.event_type} already has pipelines. To send to someone else, use a new event type.`
}

/**
 * Unified 4-step pipeline wizard. Replaces the v1 EventSelection → Templates
 * two-page hop. All draft state is local — nothing is synced to Redux
 * between steps (v1's putPipelineData-between-pages caused stale-snapshot
 * overwrites); only the final submit dispatches.
 */
const PipelineWizard = () => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const narrow = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })
    const navigate = useNavigate()
    const location = useLocation()
    const { pipelineID } = useParams()
    const isEditMode = !!pipelineID
    // Set when Create Holiday handed over a holiday: the holiday is saved
    // once its pipeline is.
    const holiday = isEditMode ? null : location.state?.holiday ?? null

    const { pipelines, eventTypes, enums } = useSelector(
        (state) => state.pipeline
    )
    const { templates } = useSelector((state) => state.template)
    const previewTemplate = useTemplatePreview()
    const [refreshingTemplates, setRefreshingTemplates] = useState(false)
    // The first load of the templates (none in the store yet): the template
    // step shows it is loading instead of "No approved templates".
    const [loadingTemplates, setLoadingTemplates] = useState(
        () => !templates?.length
    )

    const [form, setForm] = useState(EMPTY_FORM)
    const [activeStep, setActiveStep] = useState(0)
    const [activeChannelIdx, setActiveChannelIdx] = useState(0)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [hydrating, setHydrating] = useState(isEditMode)
    // The holiday's pipeline exists but saving the holiday failed.
    const [holidayPending, setHolidayPending] = useState(false)
    // Edit: the pipeline as saved, sent back with the changes on top.
    const [savedPipeline, setSavedPipeline] = useState(null)

    const patchForm = (patch) => setForm((prev) => ({ ...prev, ...patch }))

    // Reference data — fetch only if the store doesn't already have it.
    useEffect(() => {
        // A preview left open by another page must not pop up here.
        dispatch(clearTemplateData())
        if (!eventTypes?.length) dispatch(PipelineService.getEventTypes())
        if (!templates?.length)
            dispatch(TemplateService.getTemplates()).then(() =>
                setLoadingTemplates(false)
            )
        if (!enums) dispatch(PipelineService.getPipelineEnums())
    }, [])

    const receiverOptions = receiverTypeOptions(enums, form.receiverType)
    const preset = holiday ? '' : receiverPreset(enums, form.event_type)
    // The event type the form was filled with (an edit, or a duplicated
    // row): its saved receiver type stays even if the event's first pipeline
    // sends to another.
    const loadedEventType =
        savedPipeline?.event_type ?? location.state?.duplicate?.event_type ?? ''

    // Picking an existing event type (or the enums arriving after it was
    // picked) presets its receiver type, as the old app did.
    useEffect(() => {
        if (!preset || form.event_type === loadedEventType) return
        if (form.receiverType !== preset) onReceiverChange(preset)
    }, [preset, form.event_type])

    const onRefreshTemplates = () => {
        setRefreshingTemplates(true)
        dispatch(TemplateService.getTemplates()).then(() =>
            setRefreshingTemplates(false)
        )
    }

    // Edit-mode hydration. v1 used strict === between a string route param
    // and a numeric id and always missed — compare as strings.
    useEffect(() => {
        if (!isEditMode) {
            // "Duplicate" from the list page passes the source row via
            // location.state; prefill from it, otherwise start clean.
            // A duplicate is a new pipeline: its event type is trimmed like
            // any other new one (the saved one is kept only when editing).
            if (location.state?.duplicate) {
                const source = location.state.duplicate
                setForm({
                    ...pipelineToForm(source),
                    event_type: normalizeEventType(source.event_type),
                })
            } else if (holiday) {
                setForm(holidayToPipelineForm(holiday))
            }
            return
        }
        const findById = (list) =>
            list?.find((p) => String(p.id) === String(pipelineID))

        const existing = findById(pipelines)
        if (existing) {
            setSavedPipeline(existing)
            setForm(pipelineToForm(existing))
            setHydrating(false)
            return
        }
        // Not in the store (a reload or a shared link): fetch it by id. This
        // used to refetch the event type's list with the selected event type,
        // which is empty after a reload, so the request went to
        // /comms/pipeline/ and the edit page never loaded.
        dispatch(PipelineService.getPipeline(pipelineID)).then((resp) => {
            setHydrating(false)
            if (resp.error) {
                setError(errorMessage({ data: resp.payload }))
                return
            }
            const found = resp.payload?.data?.pipeline
            if (found) {
                setSavedPipeline(found)
                setForm(pipelineToForm(found))
            } else setError('Pipeline not found')
        })
    }, [pipelineID])

    const onToggleChannel = (commType) => {
        setForm((prevForm) => toggleChannel(prevForm, commType))
    }

    const toggleChannel = (prev, commType) => {
        {
            const exists = prev.comms.some((c) => c.comm_type === commType)
            if (isEditMode) {
                // An existing pipeline is one channel (the v2 update keeps
                // only comms[0]); switching channel replaces it.
                return exists
                    ? prev
                    : {
                          ...prev,
                          comms: [
                              {
                                  comm_type: commType,
                                  template_id: null,
                                  receiver_type: prev.receiverType,
                                  channel: '',
                              },
                          ],
                      }
            }
            return {
                ...prev,
                comms: exists
                    ? prev.comms.filter((c) => c.comm_type !== commType)
                    : [
                          ...prev.comms,
                          {
                              comm_type: commType,
                              template_id: null,
                              receiver_type: prev.receiverType,
                              channel: '',
                          },
                      ],
            }
        }
    }

    const onReceiverChange = (value) => {
        setForm((prev) => ({
            ...prev,
            receiverType: value,
            comms: prev.comms.map((c) => ({ ...c, receiver_type: value })),
        }))
    }

    const onAssignTemplate = (templateId) => {
        const template = templates?.find((t) => t.id === templateId)
        setForm((prev) => ({
            ...prev,
            comms: prev.comms.map((c, i) =>
                i === activeChannelIdx
                    ? {
                          ...c,
                          template_id: templateId,
                          channel: commChannel(c.comm_type, template),
                      }
                    : c
            ),
        }))
    }

    // Editing: the saved event type, which is kept exactly as saved unless
    // the field is changed (helpers/eventType pipelineEventType).
    const savedEventType = isEditMode ? savedPipeline?.event_type ?? null : null
    const eventTypeCheck = checkPipelineEventType(
        form.event_type,
        savedEventType
    )
    const onEventTypeChange = (value) =>
        patchForm({ event_type: pipelineEventType(value, savedEventType) })

    const stepValid = [
        trimSpaces(form.pipeline_name) !== '' && eventTypeCheck.valid,
        form.comms.length > 0 && form.receiverType !== '',
        form.comms.length > 0 && form.comms.every((c) => !!c.template_id),
        true,
    ]
    const isLastStep = activeStep === STEPS.length - 1

    const buildPayload = () => ({
        pipeline_name: pipelineName(
            form.pipeline_name,
            isEditMode ? savedPipeline?.pipeline_name ?? null : null
        ),
        event_type: pipelineEventType(form.event_type, savedEventType),
        comms: form.comms,
        schedule: form.scheduleEnabled ? Number(form.scheduleSeconds) || 0 : 0,
        niceness: form.niceness,
        absolute_time: holiday ? true : form.absolute_time,
        comment: form.comment,
        one_time: holiday ? true : form.one_time,
    })

    /**
     * After a holiday's pipeline is created: store the holiday, then go to
     * the Holidays list. If that fails the page stays, with the pipeline
     * already created, and the last button retries only the holiday.
     */
    const saveHoliday = async () => {
        const name = holiday.holiday_name || holiday.holiday_id
        setSubmitting(true)
        setError('')
        try {
            await HolidayService.create(holiday)
            navigate('/holidays', {
                state: {
                    holidayNotice: `${name} and its pipeline are created.`,
                },
            })
        } catch (err) {
            setHolidayPending(true)
            setError(
                `The pipeline was created, but the holiday was not saved: ${errorMessage(
                    err?.response
                )}`
            )
            setSubmitting(false)
        }
    }

    const onSubmit = () => {
        setSubmitting(true)
        setError('')
        const payload = isEditMode
            ? pipelineEditPayload(savedPipeline, pipelineID, buildPayload())
            : buildPayload()
        dispatch(
            isEditMode
                ? PipelineService.updatePipeline(payload)
                : PipelineService.createPipeline(payload)
        ).then((resp) => {
            if (resp.error) {
                setError(
                    `The pipeline was not saved: ${errorMessage({
                        data: resp.payload,
                    })}`
                )
                setSubmitting(false)
                return
            }
            if (holiday) saveHoliday()
            else navigate('/pipelines')
        })
    }

    const onNext = () => {
        if (holidayPending) saveHoliday()
        else if (isLastStep) onSubmit()
        else setActiveStep((s) => s + 1)
    }

    const onBack = () => {
        if (activeStep === 0) navigate('/pipelines')
        else setActiveStep((s) => s - 1)
    }

    const stepBody = [
        <StepBasics
            key="basics"
            form={form}
            eventTypes={eventTypes}
            onChange={patchForm}
            onEventTypeChange={onEventTypeChange}
            eventTypeError={eventTypeCheck.error}
            eventTypeNote={eventTypeCheck.note}
            holidayLocked={!!holiday}
        />,
        <StepChannels
            key="channels"
            form={form}
            onToggleChannel={onToggleChannel}
            onReceiverChange={onReceiverChange}
            singleChannel={isEditMode}
            receiverOptions={receiverOptions}
            receiverLockReason={receiverLockReason({
                holiday,
                preset,
                form,
                options: receiverOptions,
            })}
        />,
        <StepTemplates
            key="templates"
            form={form}
            templates={templates}
            activeIdx={activeChannelIdx}
            onActiveIdxChange={setActiveChannelIdx}
            onAssignTemplate={onAssignTemplate}
            onPreview={previewTemplate}
            onRefresh={onRefreshTemplates}
            refreshing={refreshingTemplates}
            loading={loadingTemplates}
        />,
        <StepReview
            key="review"
            form={form}
            templates={templates}
            isEditMode={isEditMode}
            receiverOptions={receiverOptions}
        />,
    ][activeStep]

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 3, md: 4 } }}>
                {/* Page header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: `${RADIUS.md}px`,
                            background: GRADIENT.brand,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {/* Deep Blue on the orange-to-yellow tile in both
                            themes (white is 3.3:1 on orange). */}
                        <PolylineIcon
                            sx={{ color: ON_ORANGE, fontSize: ICON.xl }}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="h2"
                            component="h1"
                            sx={{ color: TEXT.primary, lineHeight: 1.2 }}
                        >
                            {isEditMode ? 'Edit Pipeline' : 'New Pipeline'}
                        </Typography>
                        <Typography
                            variant="caption"
                            component="p"
                            sx={{
                                color: TEXT.faint,
                                mt: 0.25,
                            }}
                        >
                            {isEditMode
                                ? 'Update the trigger, channels, or templates'
                                : holiday
                                ? `Step 2 of 2 for holiday ${holiday.holiday_id}: the pipeline that sends its comms. The holiday is saved when this pipeline is.`
                                : 'Set up an event-triggered communication in four steps'}
                        </Typography>
                    </Box>
                </Box>

                {/* Labels go under the step numbers on a phone; beside
                    them, the five steps were wider than the screen. A done
                    step's label goes back to it, as the old wizard's
                    collapsed panels did; later steps open only by Next. */}
                <Stepper
                    activeStep={activeStep}
                    alternativeLabel={narrow}
                    sx={{ mb: 3 }}
                >
                    {STEPS.map((label, index) => (
                        <Step key={label} completed={index < activeStep}>
                            {index < activeStep ? (
                                <StepButton
                                    onClick={() => setActiveStep(index)}
                                    disabled={submitting || holidayPending}
                                >
                                    {label}
                                </StepButton>
                            ) : (
                                <StepLabel>{label}</StepLabel>
                            )}
                        </Step>
                    ))}
                </Stepper>

                {error && (
                    <Box sx={{ mb: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                <Paper
                    elevation={0}
                    sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}
                >
                    {hydrating ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 8,
                            }}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        stepBody
                    )}
                </Paper>

                {/* Navigation */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 3,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onBack}
                        disabled={holidayPending}
                        sx={{ px: 2.5 }}
                    >
                        {activeStep === 0 ? 'Cancel' : 'Back'}
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {activeStep === 2 && !stepValid[2] && (
                            <Typography
                                variant="caption"
                                sx={{ color: TEXT.faint }}
                            >
                                {
                                    form.comms.filter((c) => !c.template_id)
                                        .length
                                }{' '}
                                channel
                                {form.comms.filter((c) => !c.template_id)
                                    .length !== 1
                                    ? 's'
                                    : ''}{' '}
                                still need a template
                            </Typography>
                        )}
                        <Button
                            variant="contained"
                            onClick={onNext}
                            disabled={
                                !stepValid[activeStep] ||
                                submitting ||
                                hydrating
                            }
                            startIcon={
                                submitting ? (
                                    <CircularProgress
                                        size={14}
                                        sx={{ color: TEXT.muted }}
                                    />
                                ) : null
                            }
                            sx={{ px: 3 }}
                        >
                            {holidayPending
                                ? 'Retry saving the holiday'
                                : isLastStep
                                ? isEditMode
                                    ? 'Save Changes'
                                    : 'Launch Pipeline'
                                : 'Next'}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <TemplatePreview />
        </Box>
    )
}

export default PipelineWizard
